import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    getWorkspaceListApi,
    switchWorkspaceApi,
    listWorkspaceMembersApi,
    listWorkspaceMembersPaginatedApi,
    addWorkspaceMemberApi,
    acceptInviteApi,
    reinviteWorkspaceMemberApi,
    updateWorkspaceMemberApi,
    deleteWorkspaceMemberApi,
    validateInviteApi,
    listWorkspaceLocationsApi
} from "../apis";
import { showSnackbar } from "../slices/SnackbarSlice";


export const validateInviteAction = createAsyncThunk(
    "workspace/validateInvite",
    async (data: any, { rejectWithValue, dispatch }) => {
        try {
            const response = await validateInviteApi(data);
            return response.data;
        } catch (error: any) {
            const errorMessage = error?.response?.data?.error || error?.response?.data?.message || "Invalid or expired invitation";
            dispatch(showSnackbar({
                message: errorMessage,
                type: "error"
            }));
            return rejectWithValue(error?.response?.data || { message: errorMessage });
        }
    }
);


export const listWorkspaceMembersAction = createAsyncThunk(
    "workspace/listMembers",
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const response = await listWorkspaceMembersApi();
            return response.data;
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error?.response?.data?.message || "Failed to fetch workspace members",
                type: "error"
            }));
            return rejectWithValue(error?.response?.data || { message: "Failed to fetch workspace members" });
        }
    }
);

export const listWorkspaceMembersPaginatedAction = createAsyncThunk(
    "workspace/listMembersPaginated",
    async (data: any, { rejectWithValue, dispatch }) => {
        try {
            const response = await listWorkspaceMembersPaginatedApi(data);
            return response.data;
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error?.response?.data?.message || "Failed to fetch workspace members",
                type: "error"
            }));
            return rejectWithValue(error?.response?.data || { message: "Failed to fetch workspace members" });
        }
    }
);

export const addWorkspaceMemberAction = createAsyncThunk(
    "workspace/addMember",
    async (data: any, { rejectWithValue, dispatch }) => {
        try {
            const response = await addWorkspaceMemberApi(data);
            dispatch(showSnackbar({
                message: response?.data?.message || "Member invited successfully",
                type: "success"
            }));
            return response.data;
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error?.response?.data?.message || "Failed to invite member",
                type: "error"
            }));
            return rejectWithValue(error?.response?.data || { message: "Failed to invite member" });
        }
    }
);

export const acceptInviteAction = createAsyncThunk(
    "workspace/acceptInvite",
    async (data: any, { rejectWithValue, dispatch }) => {
        try {
            const response = await acceptInviteApi(data);
            dispatch(showSnackbar({
                message: response?.data?.message || "Invite accepted successfully",
                type: "success"
            }));
            return response.data;
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error?.response?.data?.message || "Failed to accept invite",
                type: "error"
            }));
            return rejectWithValue(error?.response?.data || { message: "Failed to accept invite" });
        }
    }
);

export const reinviteWorkspaceMemberAction = createAsyncThunk(
    "workspace/reinviteMember",
    async (id: string, { rejectWithValue, dispatch }) => {
        try {
            const response = await reinviteWorkspaceMemberApi(id);
            dispatch(showSnackbar({
                message: response?.data?.message || "Invite resent successfully",
                type: "success"
            }));
            return response.data;
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error?.response?.data?.message || "Failed to resend invite",
                type: "error"
            }));
            return rejectWithValue(error?.response?.data || { message: "Failed to resend invite" });
        }
    }
);

export const updateWorkspaceMemberAction = createAsyncThunk(
    "workspace/updateMember",
    async ({ id, data }: { id: string; data: any }, { rejectWithValue, dispatch }) => {
        try {
            const response = await updateWorkspaceMemberApi(id, data);
            dispatch(showSnackbar({
                message: response?.data?.message || "Member updated successfully",
                type: "success"
            }));
            return response.data;
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error?.response?.data?.message || "Failed to update member",
                type: "error"
            }));
            return rejectWithValue(error?.response?.data || { message: "Failed to update member" });
        }
    }
);


export const deleteWorkspaceMemberAction = createAsyncThunk(
    "workspace/deleteMember",
    async (id: string, { rejectWithValue, dispatch }) => {
        try {
            const response = await deleteWorkspaceMemberApi(id);
            dispatch(showSnackbar({
                message: response?.data?.message || "Member deleted successfully",
                type: "success"
            }));
            return response.data;
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error?.response?.data?.message || "Failed to delete member",
                type: "error"
            }));
            return rejectWithValue(error?.response?.data || { message: "Failed to delete member" });
        }
    }
);


export const getWorkspaceListAction = createAsyncThunk(
    "workspace/getList",
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const response = await getWorkspaceListApi();
            return response.data;
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error?.response?.data?.message || "Failed to fetch workspaces",
                type: "error"
            }));
            return rejectWithValue(error?.response?.data || { message: "Failed to fetch workspaces" });
        }
    }
);

export const switchWorkspaceAction = createAsyncThunk(
    "workspace/switch",
    async (workspaceId: string, { rejectWithValue, dispatch }) => {
        try {
            const response = await switchWorkspaceApi(workspaceId);
            dispatch(showSnackbar({ message: response?.data?.message || "Workspace switched successfully", type: "success" }));
            return response.data;
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error?.response?.data?.message || "Failed to switch workspace",
                type: "error"
            }));
            return rejectWithValue(error?.response?.data || { message: "Failed to switch workspace" });
        }
    }
);

export const listWorkspaceLocationsAction = createAsyncThunk(
    "workspace/listLocations",
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const response = await listWorkspaceLocationsApi();
            return response.data;
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error?.response?.data?.message || "Failed to fetch workspace locations",
                type: "error"
            }));
            return rejectWithValue(error?.response?.data || { message: "Failed to fetch workspace locations" });
        }
    }
);
